"use client";

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useSearchParams, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import AppLayoutDoctor from "../../../navigation/Layout/LayoutDoctor";
import DoctorScrollTabs from "../../../Profile/components/ScrollTabs/DoctorScrollTabs";

import usePrescriptions from "../../../prescriptions/hooks/usePrescriptions";
import { useTour } from "../../../appointments/hooks/useTour";
import { medicinesTourSteps } from "./medicinesTourConfig";

import MedicationHistoryCardDoctor from "../../components/MedicationHistoryCard/MedicationHistoryCardDoctor";
import MedicationListCardDoctor from "../../components/MedicationListCard/MedicationListCardDoctor";
import PrescriptionHistoryCard from "../../components/PrescriptionHistoryCard/PrescriptionHistoryCard";
import PrescriptionModal from "../../components/Prescriptionmodal/Prescriptionmodal";

import { usePrescriptionLayout } from "../../../prescriptions/hooks/usePrescriptionLayout";
import useMedicalCapabilities from "../../../authorization/useMedicalCapabilities";

import "./Medicines.css";

/* ================= Helpers ================= */
const normalizeId = (v) => {
  if (v == null) return null;
  if (typeof v === "object") {
    const id = v.id ?? v._id ?? v.prescriptionId ?? null;
    return id != null ? String(id).trim() : null;
  }
  const s = String(v).trim();
  return s.length ? s : null;
};

const getItemId = (p) =>
  normalizeId(p?.id) || normalizeId(p?._id) || normalizeId(p?.prescriptionId) || null;

export default function Medicines() {
  const { t } = useTranslation();

  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { patientId } = useParams();

  const userId = patientId || null;

  // ✅ vrai doctor depuis redux
  const doctorUser = useSelector((state) => state.doctor?.user);

  const doctorId = useMemo(() => {
    return (
      normalizeId(doctorUser?.id) ||
      normalizeId(doctorUser?.doctorId) ||
      normalizeId(doctorUser?.pgId) ||
      normalizeId(doctorUser?._id) ||
      null
    );
  }, [doctorUser]);

  const { capabilities, refresh: refreshCapabilities } =
    useMedicalCapabilities(userId, doctorId);

  // ✅ prescriptions: doctor view => patientId + doctorId
  const { prescriptionItems, loading: prescriptionsLoading } = usePrescriptions({
    patientId: userId,
    doctorId: doctorId,
  });

  const [shouldStartTour, setShouldStartTour] = useState(false);
  const [prescriptionModalOpen, setPrescriptionModalOpen] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);

  // ✅ Layout du doctor (template enregistré)
  const { layout: prescriptionLayout } = usePrescriptionLayout();

  useEffect(() => {
    const hasGuide = searchParams.get("guide") === "true";
    const hasTour = searchParams.get("tour") === "medicines";
    let timer;

    if ((hasGuide || hasTour) && !shouldStartTour) {
      timer = setTimeout(() => {
        const medsSection = document.querySelector(".meds-section");
        if (medsSection) setShouldStartTour(true);
      }, 800);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [searchParams, pathname, shouldStartTour]);

  useTour(shouldStartTour, medicinesTourSteps);

  // ✅ CLICK ROBUSTE + envoie _original complet au modal
  const handlePrescriptionClick = useCallback(
    (prescriptionId) => {
      if (!prescriptionItems?.length) return;

      const pid = normalizeId(prescriptionId);
      const item = prescriptionItems.find((p) => getItemId(p) === pid);
      if (!item) return;

      const original = item._original || {};

      // ✅ contenu (priorité backend)
      const content =
        original.description ||
        original.content ||
        item.content ||
        item.name ||
        t("doctorMedicines.defaults.prescriptionContent");

      // ✅ doctor info (fallback)
      const doctorInfo = {
        id: original.medecinId || original.doctorId || item.doctorId || doctorId || null,
        fullName:
          original.fullName ||
          item.doctorName ||
          t("doctorMedicines.unknownDoctor"),
        fullNameAr: original.fullNameAr || null,
        specialty:
          original.specialty ||
          item.type ||
          t("doctorMedicines.medicalProfessional"),
        specialtyAr: original.specialtyAr || null,
        phone: original.phone || "",
        email: original.email || "",
        clinicAddress: original.address || original.clinicAddress || "",
        addressAr: original.addressAr || "",
      };

      setSelectedPrescription({
        id: getItemId(item),
        content,
        type: original.documentType || original.type || item.type,
        doctorId: doctorInfo.id,
        doctor: doctorInfo,
        layout: {
          logoUrl: original.logoUrl || original.logo_url || null,
          couleurTexte: original.couleurTexte || original.couleur_texte || null,
          couleurLogo: original.couleurLogo || original.couleur_logo || null,
          fullName: original.fullName || original.full_name || doctorInfo.fullName,
          fullNameAr: original.fullNameAr || original.full_name_ar || null,
          specialty: original.specialty || doctorInfo.specialty,
          specialtyAr: original.specialtyAr || original.specialty_ar || null,
          phone: original.phone || "",
          email: original.email || "",
          address: original.address || "",
          addressAr: original.addressAr || original.address_ar || "",
        },
        createdAt: original.createdAt || item.dateLabel,

        // ✅ garder tout le backend pour le modal/layout
        _original: {
          ...original,
          id: original.id || original._id || getItemId(item),
        },
      });

      setPrescriptionModalOpen(true);
    },
    [prescriptionItems, t, doctorId]
  );

  const handleClosePrescriptionModal = () => {
    setPrescriptionModalOpen(false);
    setSelectedPrescription(null);
  };

  // ✅ patientId requis
  if (!userId) {
    return (
      <AppLayoutDoctor skipPatientMe>
        <div className="profile">
          <div style={{ padding: 40, textAlign: "center" }}>
            <p>{t("doctorMedicines.missingPatientId")}</p>
          </div>
        </div>
      </AppLayoutDoctor>
    );
  }

  // ✅ doctorId requis pour la vue doctor
  if (!doctorId) {
    return (
      <AppLayoutDoctor skipPatientMe>
        <div className="profile">
          <div style={{ padding: 40, textAlign: "center" }}>
            <p>Doctor ID introuvable. Vérifie la session doctor / state.doctor.user.</p>
          </div>
        </div>
      </AppLayoutDoctor>
    );
  }

  return (
    <AppLayoutDoctor skipPatientMe>
      <div className="profile doctor-medicines-page">
        <DoctorScrollTabs patientId={userId} />

        <div className="meds-inner">
          <div className="meds-container">
            <div className="meds-left">
              <div className="medication-list-card">
                <MedicationListCardDoctor
                  patientId={userId}
                  title={t("doctorMedicines.cards.medicationList.title")}
                  date={new Date()}
                  canCreate={Boolean(capabilities?.medications?.create)}
                  canUpdate={Boolean(capabilities?.medications?.update)}
                  canStop={Boolean(capabilities?.medications?.stop)}
                  onAuthorizationFailure={refreshCapabilities}
                />
              </div>
            </div>

            <aside className="meds-right meds-section">
              <MedicationHistoryCardDoctor
                patientId={userId}
                title={t("doctorMedicines.cards.medicationHistory.title")}
                subtitle={t("doctorMedicines.cards.medicationHistory.subtitle")}
                placeholder={t("doctorMedicines.cards.medicationHistory.placeholder")}
              />

              <PrescriptionHistoryCard
                title="Medical Document History"
                subtitle="All medical documents issued for this patient."
                items={prescriptionItems || []}
                loading={prescriptionsLoading}
                onPrescriptionClick={handlePrescriptionClick}
                placeholder="Search medical documents"
              />
            </aside>
          </div>
        </div>

        {/* ✅ layout doctor (template) + prescriptionData complet */}
        <PrescriptionModal
          open={prescriptionModalOpen}
          onClose={handleClosePrescriptionModal}
          prescriptionData={selectedPrescription}
          layout={prescriptionLayout}
        />
      </div>
    </AppLayoutDoctor>
  );
}
